import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { PermissionsService } from '@core/services/permissions.service';
import { advancedMaintenanceEnabledGuard } from '@features/configuration/guards/advanced-maintenance-enabled.guard';

const comingSoon = () => import('@features/configuration/views/coming-soon/coming-soon.component');

export const ConfigurationRoutes: Routes = [
  {
    path: 'configuration',
    title: 'Configuración',
    data: { label: 'Configuración de la empresa', icon: 'settings', permission: 'configuration.view' },
    children: [
      {
        path: '',
        title: 'Configuración',
        loadComponent: () => import('@features/configuration/configuration.component'),
      },
      // Administrar negocio
      {
        path: 'business',
        title: 'Administrar negocio',
        data: {
          icon: 'apartment',
          color: 'accent-dark',
          description:
            'Configura owners, funcionalidades de la app, tipos de incidentes, flujos de aprobación y modalidad de mantenimiento.',
          backFallback: '/configuration',
          permission: 'configuration.business.view',
        },
        loadComponent: () => import('@features/configuration/views/business/business.component'),
        children: [
          {
            path: '',
            pathMatch: 'full',
            // El owner solo accede al flujo de aprobación; el super admin, a todo.
            redirectTo: () =>
              inject(PermissionsService).has('configuration.business.manage')
                ? 'owners'
                : 'expiration-approval-flow',
          },
          {
            path: 'owners',
            title: 'Owners',
            data: {
              label: 'Owners',
              icon: 'person',
              backFallback: '/configuration',
              permission: 'configuration.business.manage',
            },
            loadComponent: () =>
              import('@features/configuration/views/business/owners/owners.component'),
          },
          {
            path: 'app-functionality',
            title: 'Funcionalidad de la app',
            data: {
              label: 'Funcionalidad de la app',
              icon: 'apps',
              backFallback: '/configuration',
              permission: 'configuration.business.manage',
            },
            loadComponent: () =>
              import('@features/configuration/views/business/app-functionality/app-functionality.component'),
          },
          {
            path: 'incident-types',
            title: 'Tipos de incidentes en la app',
            data: {
              label: 'Tipos de incidentes en la app',
              icon: 'report',
              backFallback: '/configuration',
              permission: 'configuration.business.manage',
            },
            loadComponent: () =>
              import('@features/configuration/views/business/incident-types/incident-types.component'),
          },
          {
            path: 'expiration-approval-flow',
            title: 'Flujo de aprobación de vencimientos',
            data: {
              label: 'Flujo de aprobación de vencimientos',
              icon: 'approval',
              backFallback: '/configuration',
              permission: 'configuration.business.approvalFlow.view',
            },
            loadComponent: () =>
              import('@features/configuration/views/business/expiration-approval-flow/expiration-approval-flow.component'),
          },
          {
            path: 'maintenance-modality',
            title: 'Modalidad de mantenimiento',
            data: {
              label: 'Modalidad de mantenimiento',
              icon: 'build',
              backFallback: '/configuration',
              permission: 'configuration.business.manage',
            },
            loadComponent: () =>
              import('@features/configuration/views/business/maintenance-modality/maintenance-modality.component'),
          },
        ],
      },
      // Proveedores
      {
        path: 'suppliers',
        title: 'Proveedores',
        data: {
          icon: 'store',
          color: 'neutral',
          description:
            'Administra tipos de documentos, tipos de proveedor y tipos de régimen fiscal.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
        },
        loadComponent: () => import('@features/configuration/views/suppliers/suppliers.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'types-supplier-documents' },
          {
            path: 'types-supplier-documents',
            title: 'Tipos de documentos de proveedores',
            data: {
              label: 'Tipos de documentos',
              icon: 'document_search',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/suppliers/types-supplier-documents/types-supplier-documents.component'),
          },
          {
            path: 'types-supplier',
            title: 'Tipos de proveedores',
            data: {
              label: 'Tipos de proveedor',
              icon: 'group_search',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/suppliers/types-supplier/types-supplier.component'),
          },
          {
            path: 'types-regimes',
            title: 'Tipos de regímenes',
            data: {
              label: 'Tipos de régimen',
              icon: 'article_person',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/suppliers/types-regimes/types-regimes.component'),
          },
        ],
      },
      // Conductores
      {
        path: 'drivers',
        title: 'Conductores',
        data: {
          icon: 'badge',
          color: 'accent',
          description: 'Gestiona las EPS disponibles para el registro de conductores.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
        },
        loadComponent: () => import('@features/configuration/views/drivers/eps/eps.component'),
      },
      // Vehículos
      {
        path: 'vehicles',
        title: 'Vehículos',
        data: {
          icon: 'directions_car',
          color: 'primary',
          description:
            'Configura tipos, marcas, líneas, vencimientos, disponibilidad, esquema de llantas y centros de costos.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
        },
        loadComponent: () =>
          import('@features/configuration/views/vehicles/vehicles-config.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'vehicle-types' },
          {
            path: 'vehicle-types',
            title: 'Tipo de vehículo',
            data: {
              label: 'Tipo de vehículo',
              icon: 'category',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/vehicles/vehicle-types/vehicle-types.component'),
          },
          {
            path: 'vehicle-brands',
            title: 'Marcas de vehículo',
            data: {
              label: 'Marcas de vehículo',
              icon: 'branding_watermark',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('../../features/configuration/views/vehicles/vehicle-brands/vehicle-brands.component'),
          },
          {
            path: 'vehicle-lines',
            title: 'Línea de vehículos',
            data: {
              label: 'Línea de vehículos',
              icon: 'linear_scale',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('../../features/configuration/views/vehicles/vehicle-lines/vehicle-lines.component'),
          },
          //TODO: Revisar la eliminacion de esta ruta y su componente y demas dependencias
          // {
          //   path: 'expiration-types',
          //   title: 'Tipo de vencimientos',
          //   data: {
          //     label: 'Tipo de vencimientos',
          //     icon: 'event_busy',
          //     backFallback: '/configuration',
          //   },
          //   loadComponent: () =>
          //     import('../../features/configuration/views/vehicles/expiration-types/expiration-types.component'),
          // },
          {
            path: 'availability-status',
            title: 'Estado de disponibilidad de vehículos',
            data: {
              label: 'Estado de disponibilidad de vehículos',
              icon: 'toggle_on',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/vehicles/availability-status/availability-status.component'),
          },
          {
            path: 'tire-scheme',
            title: 'Esquema de llantas',
            data: {
              label: 'Esquema de llantas',
              icon: 'tire_repair',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/vehicles/tire-schemes/tire-schemes.component'),
          },
          {
            path: 'cost-centers',
            title: 'Centro de costos',
            data: {
              label: 'Centro de costos',
              icon: 'account_balance',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/vehicles/cost-centers/cost-centers.component'),
          },
        ],
      },
      // Combustible
      {
        path: 'fuel',
        title: 'Combustible',
        data: {
          icon: 'local_gas_station',
          color: 'secondary',
          description: 'Administra tipos de combustible, rendimiento ideal y listas de chequeo.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
        },
        loadComponent: () => import('@features/configuration/views/fuel/fuel-config.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'fuel-types' },
          {
            path: 'fuel-types',
            title: 'Tipos de combustible',
            data: {
              label: 'Tipos de combustible',
              icon: 'propane_tank',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/fuel/fuel-types/fuel-types.component'),
          },
          {
            path: 'ideal-performance',
            title: 'Rendimiento ideal',
            data: {
              label: 'Rendimiento ideal',
              icon: 'speed',
              backFallback: '/configuration',
            },
            loadComponent: comingSoon,
          },
          // {
          //   path: 'checklists',
          //   title: 'Listas de chequeo',
          //   data: {
          //     label: 'Listas de chequeo',
          //     icon: 'checklist',
          //     backFallback: '/configuration',
          //   },
          //   loadComponent: comingSoon,
          // },
        ],
      },
      // Mantenimiento avanzado
      {
        path: 'advanced-maintenance',
        title: 'Mantenimiento avanzado',
        canActivate: [advancedMaintenanceEnabledGuard],
        data: {
          icon: 'handyman',
          color: 'success',
          description:
            'Configura tipos de mantenimiento, órdenes de trabajo, sistemas, subsistemas y grupos de recursos.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
          redirectWhenDisabled: '/configuration',
        },
        loadComponent: () =>
          import('@features/configuration/views/advanced-maintenance/advanced-maintenance-config.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'maintenance-types' },
          {
            path: 'maintenance-types',
            title: 'Tipos de mantenimientos',
            data: {
              label: 'Tipos de mantenimientos',
              icon: 'construction',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/advanced-maintenance/maintenance-types/maintenance-types.component'),
          },
          {
            path: 'work-order-types',
            title: 'Tipos de órdenes de trabajo',
            data: {
              label: 'Tipos de órdenes de trabajo',
              icon: 'assignment',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/advanced-maintenance/work-order-types/work-order-types.component'),
          },
          {
            path: 'systems-subsystems',
            title: 'Sistemas y subsistemas',
            data: {
              label: 'Sistemas y subsistemas',
              icon: 'account_tree',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/advanced-maintenance/systems/systems.component'),
          },
          {
            path: 'systems-subsystems/:systemId/subsystems',
            title: 'Subsistemas',
            data: {
              backFallback: '/configuration/advanced-maintenance/systems-subsystems',
            },
            loadComponent: () =>
              import('@features/configuration/views/advanced-maintenance/subsystems/subsystems.component'),
          },
          {
            path: 'resource-groups',
            title: 'Grupo de repuestos',
            data: {
              label: 'Grupo de repuestos',
              icon: 'groups',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/advanced-maintenance/resource-groups/resource-groups.component'),
          },
        ],
      },
      // Alertas
      {
        path: 'alerts',
        title: 'Alertas',
        data: {
          icon: 'notifications',
          color: 'warning',
          description: 'Configura las alertas del sistema para la empresa.',
          backFallback: '/configuration',
          permission: 'configuration.catalogs.view',
        },
        loadComponent: () =>
          import('../../features/configuration/views/alerts/alerts-config.component'),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'vehicle-expirations' },
          {
            path: 'vehicle-expirations',
            title: 'Vencimientos de vehículos',
            data: {
              label: 'Vencimientos de vehículos',
              icon: 'directions_car',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('../../features/configuration/views/alerts/vehicle-expirations/vehicle-expiration-alerts.component'),
          },
          {
            path: 'license-expirations',
            title: 'Vencimiento de licencias',
            data: {
              label: 'Vencimiento de licencias',
              icon: 'badge',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('../../features/configuration/views/alerts/license-expirations/license-expiration-alerts.component'),
          },
          {
            path: 'checklists',
            title: 'Listas de chequeo',
            data: {
              label: 'Listas de chequeo',
              icon: 'checklist',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/alerts/checklists/checklist-alerts.component'),
          },
          {
            path: 'checklists/priority-items',
            title: 'Listas de chequeo · Items prioritarios',
            data: {
              backFallback: '/configuration/alerts/checklists',
            },
            loadComponent: () =>
              import('@features/configuration/views/alerts/checklists/priority-items/checklist-priority-items.component'),
          },
          {
            path: 'checklists/periodicity',
            title: 'Listas de chequeo · Periodicidad',
            data: {
              backFallback: '/configuration/alerts/checklists',
            },
            loadComponent: () =>
              import('@features/configuration/views/alerts/checklists/periodicity/checklist-periodicity.component'),
          },
          {
            path: 'checklists/open-days-alerts',
            title: 'Listas de chequeo · Configurar alertas por días abiertos',
            data: {
              backFallback: '/configuration/alerts/checklists',
            },
            loadComponent: () =>
              import(
                '@features/configuration/views/alerts/checklists/open-days-alerts/checklist-open-days-alerts.component'
              ),
          },
          // {
          //   path: 'checklists/alert-statuses',
          //   title: 'Listas de chequeo · Estados de las alertas',
          //   data: {
          //     backFallback: '/configuration/alerts/checklists',
          //   },
          //   loadComponent: comingSoon,
          // },
          {
            path: 'advanced-maintenance',
            title: 'Mantenimiento avanzado',
            data: {
              label: 'Mantenimiento avanzado',
              icon: 'handyman',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/alerts/advanced-maintenance-alerts/advanced-maintenance-alerts.component'),
          },
          {
            path: 'basic-maintenance',
            title: 'Mantenimiento básico',
            data: {
              label: 'Mantenimiento básico',
              icon: 'build',
              backFallback: '/configuration',
            },
            loadComponent: () =>
              import('@features/configuration/views/alerts/basic-maintenance-alerts/basic-maintenance-alerts.component'),
          },
          {
            path: 'maintenance',
            redirectTo: 'advanced-maintenance',
            pathMatch: 'full',
          },
        ],
      },
    ],
  },
];
